/* ============================================
   Conversaciones del partner — el estado real de su WhatsApp

   Era un cartel de «PRÓXIMAMENTE» con un roadmap de tres pasos. El problema
   no es que prometiera: es que no decía **en qué punto está él**, que es lo
   único accionable. Un partner sin WhatsApp conectado y uno con el canal
   andando veían exactamente la misma pantalla.

   Ahora la pantalla mira su configuración y dice una de tres cosas:

   1. **Sin conectar** — lo que le falta hacer, con el botón que lo lleva.
   2. **Conectado, sin mensajes** — está listo y esperando el primero.
   3. **Conectado, con mensajes** — los estamos recibiendo y guardando; la
      bandeja para leerlos es lo que sigue.

   El backend ya está entero detrás de esto: las conversaciones se scopean por
   `conversaciones.partner_id`, el resolver saca las credenciales de SU cuenta
   de Wazzup (`partner_wazzup_config`) y el webhook sabe de qué partner es
   cada canal. Lo que no existe todavía es la bandeja para leerlos, y eso es
   lo que la pantalla dice en vez de inventar un plazo.
   ============================================ */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { Badge, PageHeader } from '@/components/ui'
import { IconAlertCircle, IconCheck, IconExternalLink } from '@/components/icons/Icons'

const CRUMBS = [{ label: 'CRM' }, { label: 'Conversaciones' }]

const SOPORTE_WHATSAPP = '51986252983'
const SOPORTE_EMAIL = 'contacto@1conecta.com'

interface WazzupConfig {
  channel_name: string | null
  phone_number: string | null
  estado: string
  configured?: boolean
}

export default function PartnerConversaciones() {
  const api = useApi()
  const [config, setConfig] = useState<WazzupConfig | null>(null)
  const [conversaciones, setConversaciones] = useState<number | null>(null)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    let vivo = true
    Promise.all([
      api.get<WazzupConfig & { error?: string; configured?: boolean }>('/api/partner/wazzup-config')
        .catch(() => null),
      api.get<unknown[]>('/api/chat/conversaciones').catch(() => []),
    ]).then(([cfg, convs]) => {
      if (!vivo) return
      setConfig(cfg && !(cfg as any).error && (cfg as any).configured !== false ? cfg : null)
      setConversaciones(Array.isArray(convs) ? convs.length : 0)
    }).finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (cargando) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando...</p></div>
    </div>
  )

  const conectado = !!config && config.estado === 'activo'
  const hayMensajes = (conversaciones ?? 0) > 0

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={CRUMBS}
        actions={
          <a
            className="btn btn-sm btn-secondary"
            href={`https://wa.me/${SOPORTE_WHATSAPP}?text=${encodeURIComponent('Hola, quiero conectar el WhatsApp de mi agencia al panel.')}`}
            target="_blank"
            rel="noreferrer"
          >
            Hablar con soporte <IconExternalLink size={13} />
          </a>
        }
      />

      <div className="card pp-card conv-estado">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Tu WhatsApp</h3>
            <p className="pp-card__sub">
              {conectado
                ? 'Tu canal está conectado al panel. Los mensajes de tus clientes llegan acá y quedan guardados con tu cuenta.'
                : 'Todavía no conectaste un número. Mientras no lo hagas, los mensajes de tus clientes no llegan al panel.'}
            </p>
          </div>
          {conectado
            ? <Badge variant="aceptada">Conectado</Badge>
            : <Badge variant="neutral">Sin conectar</Badge>}
        </div>

        {conectado ? (
          <div className="pp-datos-filas">
            <div className="pp-fila-dato">
              <span>Canal</span>
              <strong>{config?.channel_name || '—'}</strong>
            </div>
            {config?.phone_number && (
              <div className="pp-fila-dato">
                <span>Número</span>
                <strong>{config.phone_number}</strong>
              </div>
            )}
            <div className="pp-fila-dato">
              <span>Conversaciones recibidas</span>
              <strong>{conversaciones ?? 0}</strong>
            </div>
            <div className="pp-card__pie">
              <Link to="/partner/conversaciones/bandeja" className="btn btn-primary">
                {hayMensajes ? 'Abrir la bandeja' : 'Abrir la bandeja (vacía)'}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <ol className="pp-pasos">
              <li>Crea tu cuenta en <a href="https://wazzup24.com" target="_blank" rel="noreferrer">wazzup24.com</a> y conecta tu WhatsApp con el código QR.</li>
              <li>Copia tu API Key desde <strong>Configuración → API</strong> de Wazzup.</li>
              <li>Pégala en <strong>Configuración → General</strong> de este panel y elige tu canal.</li>
            </ol>
            <div className="pp-card__pie">
              <Link to="/partner/ajustes/general" className="btn btn-primary">
                Conectar mi WhatsApp
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Qué puede esperar, sin plazos inventados: lo que ya funciona y lo
          que no. La bandeja es lo único que falta, y decirlo así es más útil
          que un roadmap de tres pasos sin fechas. */}
      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Qué funciona hoy</h3>
          </div>
        </div>
        <ul className="pp-niveles conv-lista">
          <li>
            <span className="conv-lista__marca conv-lista__marca--si"><IconCheck size={13} /></span>
            <span>
              <strong>Tu propio número.</strong> El panel usa tu cuenta de Wazzup, no la nuestra:
              tus clientes te escriben a ti y nosotros no vemos esas conversaciones.
            </span>
          </li>
          <li>
            <span className="conv-lista__marca conv-lista__marca--si"><IconCheck size={13} /></span>
            <span>
              <strong>Los mensajes se guardan.</strong> Desde que conectas el canal quedan
              registrados con tu cuenta, aunque todavía no los puedas leer acá.
            </span>
          </li>
          <li>
            <span className="conv-lista__marca conv-lista__marca--si"><IconCheck size={13} /></span>
            <span>
              <strong>Los lees y respondes desde acá.</strong> La bandeja muestra tus chats
              y las respuestas salen por tu número, no por el nuestro.
            </span>
          </li>
          <li>
            <span className="conv-lista__marca conv-lista__marca--no"><IconAlertCircle size={13} /></span>
            <span>
              <strong>Los asistentes automáticos no están incluidos.</strong> Los bots que
              contestan solos son nuestros y corren con nuestra cuenta de IA; en tu bandeja
              contestas tú.
            </span>
          </li>
        </ul>
        <div className="pp-card__pie">
          <a className="btn btn-secondary" href={`mailto:${SOPORTE_EMAIL}?subject=${encodeURIComponent('WhatsApp en el panel de partner')}`}>
            Escribir a soporte
          </a>
        </div>
      </div>
    </div>
  )
}
